import {
  Box,
  Stack,
  Typography,
  Paper,
  useTheme,
  useMediaQuery
} from "@mui/material";
import React from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import PeopleIcon from '@mui/icons-material/People';
import WorkIcon from '@mui/icons-material/Work';
import LockIcon from '@mui/icons-material/Lock';

const tabConfig = [
  { label: "Employees", value: "employees", icon: PeopleIcon },
  { label: "Jobs", value: "jobs", icon: WorkIcon }
];

function AdminPortalLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedClientData } = useSelector((state) => state?.main || {});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const currentTab = location.pathname.includes("/jobs") ? "jobs" : "employees";

  const handleTabChange = (_, newValue) => {
    navigate(`/client/${selectedClientData?.ClientId}/admin-portal/${newValue}`);
  };

  // Access Denied View
  if (selectedClientData?.IsAdmin !== 1) {
    return (
      <Box
        sx={{
          height: "100vh",
          background: "#F4F6F8",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2
        }}>
        <Paper
          elevation={0}
          sx={{
            p: 5,
            maxWidth: 450,
            textAlign: "center",
            background: "white",
            borderRadius: 3,
            border: "1px solid #E0E0E0",
            boxShadow: "0 4px 20px rgba(0,0,0,0.05)"
          }}>
          <Box
            sx={{
              width: 70,
              height: 70,
              margin: "0 auto 24px",
              background: "#E3F2FD",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#1565C0"
            }}>
            <LockIcon sx={{ fontSize: 36 }} />
          </Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              mb: 1,
              color: "#1a202c"
            }}>
            Admin Access Required
          </Typography>
          <Typography  sx={{color: "text.secondary",  mt: 1, lineHeight: 1.6 }}>
            You don't have permission to access the admin portal. Please contact your administrator
            if you believe this is an error.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "calc(100vh - 72px)",
        display: "flex",
        flexDirection: "column",
        background: "#F4F6F8",
        overflow: "hidden" // Prevents the whole page from scrolling
      }}>
      
      {/* Header Section - Sticky */}
      <Paper
        elevation={0}
        sx={{
          background: "white",
          borderBottom: "1px solid #E0E0E0",
          flexShrink: 0,
          zIndex: 100
        }}>
          {/* Tabs Row */}
          <Stack direction="row" spacing={1} sx={{ borderBottom: "1px solid #E0E0E0" }}>
            {tabConfig.map((tab) => {
              const Icon = tab.icon;
              const isActive = currentTab === tab.value;
              
              return (
                <Box
                  key={tab.value}
                  onClick={() => handleTabChange(null, tab.value)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 2.5,
                    py: 1.5,
                    cursor: "pointer",
                    position: "relative",
                    top: "1px",
                    color: isActive ? "#1a202c" : "#637381",
                    fontWeight: isActive ? 700 : 500,
                    fontSize: "14px",
                    transition: "all 0.2s",
                    borderBottom: isActive ? "2px solid #1a202c" : "2px solid transparent",
                    "&:hover": {
                      color: "#1a202c",
                      background: isActive ? "transparent" : "rgba(0,0,0,0.02)"
                    }
                  }}>
                  <Icon sx={{ fontSize: 18 }} />
                  {!isMobile && <span>{tab.label}</span>}
                </Box>
              );
            })}
          </Stack>
      </Paper>

      {/* Content Section - Scrollable Area */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto", // Only this section scrolls
          overflowX: "hidden",
          pt: 1.5,
        }}>
        <Outlet />
      </Box>
    </Box>
  );
}

export default AdminPortalLayout;